import React from 'react'
import { XStack, YStack } from 'tamagui'

import { Card } from '@newComponents/Card'
import { Icon } from '@newComponents/Icon'
import { Paragraph } from '@newComponents/Text'
import { Link } from '@newComponents/Link'

import { DocSection } from '../shared/DocSection'
import { DocScreenshot } from '../shared/DocScreenshot'
import { DocStep } from '../shared/DocStep'

const Messaging = () => (
  <>
    <Card gap='$4'>
      <Paragraph size='$5'>
        Guild's messaging feature lets Organizers send messages directly to
        their Guild's Members. This is a powerful way to keep your community
        informed about upcoming Events, share announcements, and maintain
        engagement between Events.
      </Paragraph>
      <Paragraph size='$5'>
        Only <Link href='/docs/guilds/members'>Organizers</Link> can send
        messages to the Guild's membership. Messages are delivered as email
        notifications to all Members of the Guild.
      </Paragraph>
      <DocScreenshot
        src='docs/guilds/guild-settings-message.png'
        alt='Guild Messaging compose screen with subject line and rich text body fields for sending messages to all Guild Members'
        caption='The message compose screen for sending announcements to Guild Members'
      />
    </Card>

    <Card gap='$4'>
      <DocSection id='sending-a-message' title='Sending a Message'>
        <DocStep
          number={1}
          title='Open Guild Settings'
          description="Navigate to your Guild's profile page and click the settings gear icon."
        />
        <DocStep
          number={2}
          title='Go to the Messaging section'
          description='In Settings, navigate to the Messaging section.'
        />
        <DocStep
          number={3}
          title='Compose your message'
          description='Write a subject line and the body of your message. The message body supports rich text formatting so you can include links, emphasis, and structure.'
        />
        <DocStep
          number={4}
          title='Send the message'
          description="Click Send to deliver the message to all Guild Members via email. Members receive the message at the email address associated with their Guild account."
        />
      </DocSection>
    </Card>

    <Card gap='$4'>
      <DocSection id='use-cases' title='When to Use Messaging'>
        <YStack gap='$3'>
          <XStack gap='$3' alignItems='flex-start'>
            <Icon name='calendar' size='$5' color='$color' flexShrink={0} marginTop={2} />
            <Paragraph size='$5'>
              <Paragraph size='$5' fontWeight='$7'>Event announcements</Paragraph> —
              Let your community know about a new or upcoming{' '}
              <Link href='/docs/events/overview'>Event</Link>. While Guild
              automatically notifies Members about new Events, a personal message
              from an Organizer can provide additional context or build excitement.
            </Paragraph>
          </XStack>
          <XStack gap='$3' alignItems='flex-start'>
            <Icon name='people-community' size='$5' color='$color' flexShrink={0} marginTop={2} />
            <Paragraph size='$5'>
              <Paragraph size='$5' fontWeight='$7'>Community updates</Paragraph> —
              Share news about changes to the Guild, new partnerships, leadership
              updates, or milestones like reaching a Member count goal.
            </Paragraph>
          </XStack>
          <XStack gap='$3' alignItems='flex-start'>
            <Icon name='projection-screen' size='$5' color='$color' flexShrink={0} marginTop={2} />
            <Paragraph size='$5'>
              <Paragraph size='$5' fontWeight='$7'>Calls for speakers</Paragraph> —
              If you're looking for presenters for an upcoming Event, send a message
              inviting Members to submit talk proposals.
            </Paragraph>
          </XStack>
          <XStack gap='$3' alignItems='flex-start'>
            <Icon name='search' size='$5' color='$color' flexShrink={0} marginTop={2} />
            <Paragraph size='$5'>
              <Paragraph size='$5' fontWeight='$7'>Feedback requests</Paragraph> —
              Ask your community for input on topics they'd like to see covered,
              preferred Event formats, or venue suggestions.
            </Paragraph>
          </XStack>
        </YStack>
      </DocSection>
    </Card>

    <Card gap='$4'>
      <DocSection id='messaging-best-practices' title='Best Practices'>
        <Paragraph size='$5'>
          Keep messages focused and relevant to your community. Members who
          receive too many messages may disengage, so aim for quality over
          quantity. A good cadence is to message when you have something genuinely
          useful to share — a new Event, an important update, or a time-sensitive
          opportunity.
        </Paragraph>
        <Paragraph size='$5'>
          Include clear calls to action in your messages. If you're announcing an
          Event, link directly to the Event page. If you're requesting feedback,
          explain how Members can respond.
        </Paragraph>
      </DocSection>
    </Card>
  </>
)

export default Messaging
